import type { ReactNode } from 'react';
import { RowActionButton, TableEmptyState } from '@practium/ui';
import styles from './ParamsTable.module.css';

export interface ReferenceColumn<T> {
	key: string;
	header: string;
	alignEnd?: boolean;
	render: (row: T) => ReactNode;
}

interface ReferenceTableProps<T> {
	rows: T[];
	columns: ReferenceColumn<T>[];
	getRowId: (row: T) => string;
	emptyTitle: string;
	emptySubtitle: string;
	onEdit?: (row: T) => void;
	onDelete?: (row: T) => void;
}

export const ReferenceTable = <T,>({
	rows,
	columns,
	getRowId,
	emptyTitle,
	emptySubtitle,
	onEdit,
	onDelete,
}: ReferenceTableProps<T>) => {
	if (rows.length === 0) return <TableEmptyState title={emptyTitle} subtitle={emptySubtitle} />;

	const hasActions = !!(onEdit || onDelete);

	return (
		<div className={styles.tableWrapper}>
			<table className={styles.table}>
				<thead className={styles.thead}>
					<tr>
						{columns.map((column) => (
							<th key={column.key} className={column.alignEnd ? styles.alignEnd : undefined}>
								{column.header}
							</th>
						))}
						{hasActions && <th className={styles.actionsHead} aria-label="Ações" />}
					</tr>
				</thead>
				<tbody className={styles.tbody}>
					{rows.map((row) => (
						<tr
							key={getRowId(row)}
							className={onEdit ? styles.clickableRow : undefined}
							onClick={onEdit ? () => onEdit(row) : undefined}
						>
							{columns.map((column) => (
								<td key={column.key} className={column.alignEnd ? styles.alignEnd : undefined}>
									{column.render(row)}
								</td>
							))}
							{hasActions && (
								<td onClick={(event) => event.stopPropagation()}>
									<div className={styles.actions}>
										{onEdit && <RowActionButton variant="edit" title="Editar" onClick={() => onEdit(row)} />}
										{onDelete && <RowActionButton variant="delete" title="Excluir" onClick={() => onDelete(row)} />}
									</div>
								</td>
							)}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};
